import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { AlertRow } from '../shell/AlertRow';
import { Spinner } from '../Spinner';
import { ErrorMessage } from '../ErrorMessage';
import { RelationsTable } from './RelationsTable';
import { WarHistoryTable } from './WarHistoryTable';
import { WarBenefitsPanel } from './WarBenefitsPanel';
import { tabStyle, btnStyle, AT_WAR_COLOR, WAR_BORDER_COLOR, CEASEFIRE_COLOR } from './relationsPresentation';
import type { RelationsShellProps, RelationsTab } from '../../pages/Relations';

const TABS: { key: RelationsTab; label: (activeWar: boolean) => string }[] = [
  { key: 'overview', label: () => 'Overview' },
  { key: 'war', label: (activeWar) => `War${activeWar ? ' ⚔' : ''}` },
  { key: 'history', label: () => 'History' },
];

/**
 * SF-158: thin desktop wrapper — no layout redesign, existing grid/table/flex-wrap button row
 * kept, but the same correctness fixes as MobileRelationsShell land here too (ARIA linking,
 * scroll-reset, Kingdom/Island split, declareBlockedReason, formatGameDate, decolored ledger,
 * AlertRow empty state) since none of them is a viewport-driven decision.
 */
export function DesktopRelationsShell({
  data,
  isLoading,
  error,
  activeTab,
  setActiveTab,
  busyAction,
  isMonarch,
  activeWar,
  declareKingdomNumber,
  setDeclareKingdomNumber,
  declareIslandNumber,
  setDeclareIslandNumber,
  declareBlockedReason,
  warStartedText,
  mutationVersion,
  handleDeclareWarRequest,
  handleProposePeace,
  handleWithdrawRequest,
  handleCancelCeasefireRequest,
}: RelationsShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  useEffect(() => {
    if (mutationVersion > 0) {
      h1Ref.current?.focus();
    }
  }, [mutationVersion]);

  const declareWarBusy = busyAction === 'declareWar';
  const proposePeaceBusy = busyAction === 'proposePeace';
  const withdrawBusy = busyAction === 'withdraw';
  const cancelCeasefireBusy = busyAction === 'cancelCeasefire';

  const declareDisabled = declareWarBusy || !declareKingdomNumber.trim() || !declareIslandNumber.trim();

  return (
    <div style={{ padding: 16 }}>
      <h1
        ref={h1Ref}
        tabIndex={-1}
        className="outline-none"
        style={{ margin: '0 0 12px', color: COLORS.text.primary, fontSize: 18 }}
      >
        Relations
      </h1>

      {/* Tab bar */}
      <div
        role="tablist"
        aria-label="Relations sections"
        style={{ display: 'flex', borderBottom: `1px solid ${COLORS.border}`, marginBottom: 16, gap: 0 }}
      >
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            id={`relations-tab-${key}`}
            role="tab"
            aria-selected={activeTab === key}
            aria-controls={`relations-panel-${key}`}
            onClick={() => {
              setActiveTab(key);
              window.scrollTo(0, 0);
            }}
            style={tabStyle(activeTab === key)}
          >
            {label(Boolean(activeWar))}
          </button>
        ))}
      </div>

      {isLoading && !data && <Spinner />}
      {error && !data && <ErrorMessage message={error} />}

      {/* ─── Overview Tab ─────────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div role="tabpanel" id="relations-panel-overview" aria-labelledby="relations-tab-overview">
          {data && (
            <>
              <RelationsTable relationships={data.relationships} activeWar={activeWar} layout="table" />

              {!activeWar && isMonarch && (
                <div
                  style={{
                    marginTop: 20,
                    padding: 12,
                    backgroundColor: COLORS.card,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 6,
                  }}
                >
                  <div style={{ fontSize: 12, color: COLORS.text.tertiary, marginBottom: 8 }}>
                    Declare War (Monarch only) — target kingdom must be Hostile toward you
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={declareKingdomNumber}
                      onChange={(e) => setDeclareKingdomNumber(e.target.value)}
                      placeholder="Kingdom"
                      style={{
                        padding: '5px 8px',
                        fontSize: 12,
                        backgroundColor: COLORS.cardAlt,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 4,
                        color: COLORS.text.primary,
                        width: 80,
                      }}
                    />
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={declareIslandNumber}
                      onChange={(e) => setDeclareIslandNumber(e.target.value)}
                      placeholder="Island"
                      style={{
                        padding: '5px 8px',
                        fontSize: 12,
                        backgroundColor: COLORS.cardAlt,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 4,
                        color: COLORS.text.primary,
                        width: 80,
                      }}
                    />
                    <button
                      onClick={handleDeclareWarRequest}
                      disabled={declareDisabled}
                      aria-busy={declareWarBusy}
                      style={btnStyle('danger', declareDisabled)}
                    >
                      Declare War
                    </button>
                  </div>
                  {declareBlockedReason && (
                    <div style={{ fontSize: 12, color: COLORS.warning, marginTop: 6 }}>{declareBlockedReason}</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ─── War Tab ──────────────────────────────────────────────────────────── */}
      {activeTab === 'war' && (
        <div role="tabpanel" id="relations-panel-war" aria-labelledby="relations-tab-war">
          {activeWar ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div
                style={{
                  padding: 12,
                  backgroundColor: COLORS.card,
                  border: `1px solid ${WAR_BORDER_COLOR}`,
                  borderRadius: 6,
                }}
              >
                <div style={{ fontSize: 12, color: COLORS.text.tertiary, marginBottom: 4 }}>
                  At War
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: AT_WAR_COLOR }}>
                  vs {activeWar.opponentKingdomName ?? activeWar.opponentKingdomId} ({activeWar.opponentKingdomId})
                </div>
                <div style={{ fontSize: 12, color: COLORS.text.secondary, marginTop: 4 }}>
                  Started {warStartedText}
                  {activeWar.inCeasefire && (
                    <span style={{ marginLeft: 8, color: CEASEFIRE_COLOR, fontWeight: 600 }}>
                      · Ceasefire
                    </span>
                  )}
                </div>
              </div>

              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {[
                    { label: 'Land', value: `Captured ${activeWar.ledgerSummary.totalLandCaptured.toLocaleString()} · Lost ${activeWar.ledgerSummary.totalLandLost.toLocaleString()}` },
                    { label: 'Casualties', value: `Ours ${activeWar.ledgerSummary.totalCasualties.toLocaleString()} · Theirs ${activeWar.ledgerSummary.totalOpponentCasualties.toLocaleString()}` },
                  ].map(({ label, value }) => (
                    <div
                      key={label}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: COLORS.card,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 4,
                      }}
                    >
                      <div style={{ fontSize: 10, color: COLORS.text.tertiary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {label}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text.primary, marginTop: 2 }}>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: COLORS.text.tertiary, marginTop: 6 }}>
                  † Not yet tracked — coming soon
                </div>
              </div>

              <WarBenefitsPanel activeWar={activeWar} />

              {isMonarch && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {!activeWar.inCeasefire && (
                    <>
                      <button
                        onClick={handleProposePeace}
                        disabled={proposePeaceBusy}
                        aria-busy={proposePeaceBusy}
                        style={btnStyle('secondary', proposePeaceBusy)}
                      >
                        Propose Mutual Peace
                      </button>
                      <button
                        onClick={handleWithdrawRequest}
                        disabled={withdrawBusy}
                        aria-busy={withdrawBusy}
                        style={btnStyle('secondary', withdrawBusy)}
                      >
                        Withdraw
                      </button>
                    </>
                  )}
                  {activeWar.inCeasefire && (
                    <button
                      onClick={handleCancelCeasefireRequest}
                      disabled={cancelCeasefireBusy}
                      aria-busy={cancelCeasefireBusy}
                      style={btnStyle('secondary', cancelCeasefireBusy)}
                    >
                      Cancel Ceasefire
                    </button>
                  )}
                </div>
              )}
              {!isMonarch && (
                <p style={{ fontSize: 12, color: COLORS.text.tertiary }}>
                  War actions require Monarch status.
                </p>
              )}
            </div>
          ) : (
            <AlertRow severity="info" message="Your kingdom is not currently at war." />
          )}
        </div>
      )}

      {/* ─── History Tab ──────────────────────────────────────────────────────── */}
      {activeTab === 'history' && (
        <div role="tabpanel" id="relations-panel-history" aria-labelledby="relations-tab-history">
          {data && <WarHistoryTable warHistory={data.warHistory} layout="table" />}
        </div>
      )}
    </div>
  );
}
